import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '@algolia/autocomplete-theme-classic/dist/theme.css';
import './SearchAutocomplete.css';
import { isAlgoliaConfigured } from '../../lib/search/algoliaClient';
import { createProductAutocomplete } from '../../lib/search/createProductAutocomplete';
import { buildCategoriesSearchUrl } from '../../lib/search/searchRoutes';

/**
 * Barre de recherche navbar — @algolia/autocomplete-js.
 * Panneau sous la navbar, sans overlay sur la page.
 */
const SearchAutocomplete = ({
  value = '',
  onChange,
  onSubmit,
  navRef,
  className = '',
  inputClassName = '',
  showIcon = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const queryRef = useRef(value);

  onChangeRef.current = onChange;

  const handleSearch = useCallback(
    (query) => {
      const trimmed = query.trim();
      if (!trimmed) {
        navigate('/categories');
        return;
      }
      navigate(buildCategoriesSearchUrl(trimmed));
    },
    [navigate],
  );

  const handleProductSelect = useCallback(
    (slug) => {
      navigate(`/product/${slug}`);
    },
    [navigate],
  );

  useEffect(() => {
    if (!isAlgoliaConfigured() || !containerRef.current) {
      return undefined;
    }

    const instance = createProductAutocomplete({
      container: containerRef.current,
      navRef,
      initialQuery: value,
      t,
      onQueryChange: (query) => {
        queryRef.current = query;
        onChangeRef.current?.(query);
      },
      onSearch: handleSearch,
      onProductSelect: handleProductSelect,
    });

    autocompleteRef.current = instance;

    return () => {
      instance.destroy();
      autocompleteRef.current = null;
    };
  }, [handleProductSelect, handleSearch, navRef, t]);

  useEffect(() => {
    const instance = autocompleteRef.current;
    if (!instance?.setQuery || queryRef.current === value) return;
    instance.setQuery(value);
    queryRef.current = value;
  }, [value]);

  if (!isAlgoliaConfigured()) {
    return (
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit?.(event);
        }}
        className={`relative ${className}`}
        role="search"
      >
        <input
          type="search"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={t('navbar.search_placeholder')}
          aria-label={t('navbar.search_placeholder')}
          className={inputClassName}
          autoComplete="off"
        />
        {showIcon && (
          <button
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            aria-label={t('navbar.search_placeholder')}
          >
            <img className="w-4 h-4" src="/svg/nav-search.svg" alt="" />
          </button>
        )}
      </form>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`kilim-search-autocomplete ${showIcon ? '' : 'kilim-search-autocomplete--no-submit'} ${className}`}
      data-testid="kilim-search-autocomplete"
    />
  );
};

export default SearchAutocomplete;
